import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { Template } from "types/landlord/verificationTemplate";
import { getErrorMessage } from "utils/error";
import { changeApplicationTemplate, getVerificationTemplates } from "./api";
import { ChangeTemplateWarningModal } from "./ChangeTemplateWarningModal";
import { ApplicationStatus } from "shared/types";

export const ChangeTemplateSubmenu = ({
  applicationId,
  applicationStatus,
  currentTemplate,
  onTemplateChanged,
  contentClassName
}: {
  applicationId: string;
  applicationStatus?: ApplicationStatus;
  currentTemplate?: Template;
  onTemplateChanged?: () => void;
  contentClassName?: string;
}) => {
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const queryClient = useQueryClient();

  const { data: templates, isLoading } = useQuery({
    queryKey: ["VERIFICATION_TEMPLATES_LIST"],
    queryFn: getVerificationTemplates
  });

  const { mutate: changeTemplate, isPending } = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error("No template selected");
      return changeApplicationTemplate(applicationId, selectedTemplateId);
    },
    mutationKey: ["CHANGE_APPLICATION_TEMPLATE", applicationId],
    onSuccess: (data) => {
      if (data.warnings && data.warnings.length > 0)
        data.warnings.forEach((warning) => {
          toast.warning(warning.message);
        });
      else toast.success("Template changed successfully");

      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICANTS_TABLE"]
      });

      onTemplateChanged?.();
      setShowWarningModal(false);
      setSelectedTemplateId(null);

      // Force page reload after template change
      window.location.reload();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const availableTemplates = (templates?.items ?? []).filter(
    (template) =>
      !(
        template.id === currentTemplate?.id &&
        (template.version ?? 0) <= (currentTemplate.version ?? 0)
      )
  );

  const isStatusInvalid = !Boolean(
    applicationId &&
    applicationStatus &&
    [ApplicationStatus.Submitted].includes(applicationStatus)
  );

  const hasNoAvailableTemplates = !isLoading && availableTemplates.length === 0;
  const disabled = isStatusInvalid || hasNoAvailableTemplates;

  let tooltipMessage: string | null = null;
  if (hasNoAvailableTemplates)
    tooltipMessage = "There are no other templates available to change to";
  else if (isStatusInvalid)
    tooltipMessage =
      "Template change is not available for partially submitted, not submitted, decisioned, canceled, and on-hold applications";

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setShowWarningModal(true);
  };

  const handleConfirmChange = () => {
    changeTemplate();
  };

  if (isLoading) return null;

  return (
    <>
      <Tooltip open={disabled ? undefined : false} placement="bottom-start">
        <TooltipTrigger asChild>
          <div>
            <ContextMenuPopover
              placement="right-start"
              disabled={disabled || isLoading}
              contentClassName={contentClassName}
              cbTrigger={() => (
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  className="w-full"
                  disabled={disabled}
                >
                  Change template
                </Button>
              )}
              actions={availableTemplates.map((template) => ({
                title: (
                  <div className="space-y-2">
                    <div className="text-base font-medium text-content-1000">
                      {template.name}
                    </div>
                    {currentTemplate?.id === template.id &&
                    (template.version ?? 0) > (currentTemplate.version ?? 0) ? (
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary-500" />
                        <span className="text-xs font-normal text-content-1000">
                          New version of current template available
                        </span>
                      </div>
                    ) : null}
                  </div>
                ),
                onClick: () => handleTemplateSelect(template.id)
              }))}
            />
          </div>
        </TooltipTrigger>
        {tooltipMessage ? (
          <TooltipContent className="max-w-[400px] rounded-lg border bg-alpha-800 p-4 text-sm text-white z-[106]">
            {tooltipMessage}
          </TooltipContent>
        ) : null}
      </Tooltip>

      <ChangeTemplateWarningModal
        opened={showWarningModal}
        onClose={() => {
          setShowWarningModal(false);
          setSelectedTemplateId(null);
        }}
        onContinue={handleConfirmChange}
        isPending={isPending}
      />
    </>
  );
};
